import { FC, useMemo, useState } from "react";

import {
  Box,
  Checkbox,
  TableCell,
  TableRow,
  Typography,
  Chip,
  TextField,
  Alert,
} from "@mui/material";

import { ContractData, UnitRow } from "./Types";

import TableDesign, {
  TableHeading,
} from "../../../../../components/common/table/TableDesign";

const tableHeadings: TableHeading[] = [
  { label: "Select", align: "left" },
  { label: "Reference", align: "left" },
  { label: "Storage Size", align: "left" },
  { label: "Storage Type", align: "left" },
  { label: "Status", align: "left" },
  { label: "In", align: "left" },
  { label: "Out", align: "left" },
  { label: "Provisional", align: "left" },
  { label: "Invoiced Up to", align: "left" },
];

interface SelectUnitStepProps {
  contractData: ContractData;

  onFieldChange: (field: keyof ContractData, value: string | boolean) => void;

  units: UnitRow[];

  selectedUnitIds: string[];

  allUnitsSelected: boolean;

  onToggleUnit: (unitId: string) => void;

  onToggleAllUnits: () => void;

  onContinue: () => void;

  onCancel: () => void;

  onUnitChange?: (
    unitId: string,
    field: keyof UnitRow,
    value: string | boolean | null,
  ) => void;
}

const SelectUnitStep: FC<SelectUnitStepProps> = ({
  units,
  selectedUnitIds,
  onToggleUnit,
  onToggleAllUnits,
  onUnitChange,
  onContinue,
}) => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case "available":
        return {
          backgroundColor: "#E8F7F1",
          color: "#02906f",
        };

      case "occupied":
        return {
          backgroundColor: "#FFF3E0",
          color: "#E67E22",
        };

      case "self-storage":
        return {
          backgroundColor: "#E8F0FE",
          color: "#356AE6",
        };

      case "damaged":
        return {
          backgroundColor: "#FDECEC",
          color: "#D32F2F",
        };

      case "not to rent":
        return {
          backgroundColor: "#F1F1F1",
          color: "#666",
        };

      case "unsure":
        return {
          backgroundColor: "#FFF8E1",
          color: "#B8860B",
        };

      default:
        return {
          backgroundColor: "#F5F5F5",
          color: "#666",
        };
    }
  };

  const isUnitAvailable = (unit: UnitRow) => {
    return unit.status?.toLowerCase() === "available";
  };

  const availableUnits = useMemo(() => units.filter(isUnitAvailable), [units]);

  const allAvailableUnitsSelected =
    availableUnits.length > 0 &&
    availableUnits.every((unit) => selectedUnitIds.includes(unit.id));

  const formatDateForInput = (value?: string) => {
    if (!value) {
      return "";
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toISOString().split("T")[0];
  };

  /**
   * Validate only selected units.
   */
  const validateSelectedUnits = () => {
    const errors: Record<string, string> = {};

    const selectedUnits = units.filter((unit) =>
      selectedUnitIds.includes(unit.id),
    );

    if (selectedUnits.length === 0) {
      setValidationErrors({
        general: "Please select at least one unit.",
      });

      return false;
    }

    selectedUnits.forEach((unit) => {
      if (!unit.inDate) {
        errors[`${unit.id}-inDate`] = "In date is required";
      }

      // if (!unit.inTime) {
      //   errors[`${unit.id}-inTime`] = "In time is required";
      // }

      if (!unit.outDate) {
        errors[`${unit.id}-outDate`] = "Out date is required";
      }

      if (!unit.outTime) {
        errors[`${unit.id}-outTime`] = "Out time is required";
      }

      if (unit.inDate && unit.outDate) {
        const inDateTime = new Date(`${unit.inDate}T${unit.inTime || "00:00"}`);

        const outDateTime = new Date(
          `${unit.outDate}T${unit.outTime || "00:00"}`,
        );

        if (
          !Number.isNaN(inDateTime.getTime()) &&
          !Number.isNaN(outDateTime.getTime()) &&
          outDateTime <= inDateTime
        ) {
          errors[unit.id] = "Out date/time must be after In date/time";
        }
      }
    });

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleContinue = () => {
    const isValid = validateSelectedUnits();

    if (!isValid) {
      return;
    }

    onContinue();
  };

  const handleUnitFieldChange = (
    unitId: string,
    field: keyof UnitRow,
    value: string | boolean | null,
  ) => {
    onUnitChange?.(unitId, field, value);

    /**
     * Remove validation error immediately when user fixes field.
     */
    setValidationErrors((previous) => {
      const next = { ...previous };

      delete next[`${unitId}-${String(field)}`];
      delete next[unitId];
      delete next.general;

      return next;
    });
  };

  const inputStyle = {
    height: "36px",
    width: "145px",
    padding: "0 8px",
    border: "1px solid #C4C4C4",
    borderRadius: "4px",
    fontSize: "13px",
    fontFamily: "inherit",
    boxSizing: "border-box" as const,
  };

  return (
    <>
      {/* Select All */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
        }}
      >
        <Checkbox
          checked={allAvailableUnitsSelected}
          disabled={availableUnits.length === 0}
          onChange={onToggleAllUnits}
          size="small"
        />

        <Box
          component="span"
          sx={{
            fontSize: "13px",
            fontWeight: 500,
          }}
        >
          Select all available units
        </Box>
      </Box>

      {/* General validation */}
      {validationErrors.general && (
        <Alert
          severity="error"
          sx={{
            mb: 1.5,
            fontSize: "13px",
          }}
        >
          {validationErrors.general}
        </Alert>
      )}

      <TableDesign
        tableHeadings={tableHeadings}
        tableHeadColor="#02906f"
        cellColor="#fff"
        filterOption={null}
        setFilterOption={() => {}}
        stickyHeader
      >
        {units.length > 0 ? (
          units.map((unit) => {
            const available = isUnitAvailable(unit);

            const selected = selectedUnitIds.includes(unit.id);

            const statusStyle = getStatusColor(unit.status);

            const inDateError = validationErrors[`${unit.id}-inDate`];

            const inTimeError = validationErrors[`${unit.id}-inTime`];

            const outDateError = validationErrors[`${unit.id}-outDate`];

            const outTimeError = validationErrors[`${unit.id}-outTime`];

            const unitDateTimeError = validationErrors[unit.id];

            return (
              <TableRow
                key={unit.id}
                sx={{
                  backgroundColor: selected ? "#F0FAF7" : "#fff",

                  "&:hover": {
                    backgroundColor: available ? "#F8FCFB" : "#fff",
                  },
                }}
              >
                {/* Select */}
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected}
                    disabled={!available}
                    onChange={() => onToggleUnit(unit.id)}
                    size="small"
                  />
                </TableCell>
                {/* Reference */}
                <TableCell>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 500,
                    }}
                  >
                    {unit.unit_ref_id || "-"}
                  </Typography>
                </TableCell>
                {/* Storage Size */}
                <TableCell>
                  <Typography sx={{ fontSize: "13px" }}>
                    {unit.size_ft3 ? `${unit.size_ft3} ft³` : "-"}
                  </Typography>
                </TableCell>
                {/* Storage Type */}
                <TableCell>
                  <Typography sx={{ fontSize: "13px" }}>
                    {unit.storage_type || "-"}
                  </Typography>
                </TableCell>
                {/* Status */}
                <TableCell>
                  <Chip
                    label={unit.status || "-"}
                    size="small"
                    sx={{
                      fontSize: "11px",
                      height: "24px",
                      fontWeight: 500,
                      ...statusStyle,
                    }}
                  />
                </TableCell>
                {/* IN */}
                <TableCell>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    <TextField
                      type="date"
                      size="small"
                      value={formatDateForInput(unit.inDate)}
                      onChange={(event) =>
                        handleUnitFieldChange(
                          unit.id,
                          "inDate",
                          event.target.value,
                        )
                      }
                      disabled={!selected}
                      error={Boolean(inDateError)}
                      helperText={selected && inDateError ? inDateError : ""}
                      sx={{
                        width: "150px",
                        "& .MuiInputBase-input": {
                          fontSize: "13px",
                          height: "20px",
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: "10px",
                          marginLeft: 0,
                        },
                      }}
                    />

                    <TextField
                      type="time"
                      size="small"
                      value={unit.inTime || ""}
                      onChange={(event) =>
                        handleUnitFieldChange(
                          unit.id,
                          "inTime",
                          event.target.value,
                        )
                      }
                      disabled={!selected}
                      error={Boolean(inTimeError)}
                      helperText={selected && inTimeError ? inTimeError : ""}
                      sx={{
                        width: "150px",
                        "& .MuiInputBase-input": {
                          fontSize: "13px",
                          height: "20px",
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: "10px",
                          marginLeft: 0,
                        },
                      }}
                    />
                  </Box>
                </TableCell>
                {/* OUT */}
                <TableCell>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    <TextField
                      type="date"
                      size="small"
                      value={formatDateForInput(unit.outDate)}
                      onChange={(event) =>
                        handleUnitFieldChange(
                          unit.id,
                          "outDate",
                          event.target.value,
                        )
                      }
                      disabled={!selected}
                      error={Boolean(outDateError)}
                      helperText={selected && outDateError ? outDateError : ""}
                      sx={{
                        width: "150px",
                        "& .MuiInputBase-input": {
                          fontSize: "13px",
                          height: "20px",
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: "10px",
                          marginLeft: 0,
                        },
                      }}
                    />

                    <TextField
                      type="time"
                      size="small"
                      value={unit.outTime || ""}
                      onChange={(event) =>
                        handleUnitFieldChange(
                          unit.id,
                          "outTime",
                          event.target.value,
                        )
                      }
                      disabled={!selected}
                      error={Boolean(outTimeError)}
                      helperText={selected && outTimeError ? outTimeError : ""}
                      sx={{
                        width: "150px",
                        "& .MuiInputBase-input": {
                          fontSize: "13px",
                          height: "20px",
                        },
                        "& .MuiFormHelperText-root": {
                          fontSize: "10px",
                          marginLeft: 0,
                        },
                      }}
                    />

                    {selected && unitDateTimeError && (
                      <Typography
                        sx={{
                          fontSize: "10px",
                          color: "#d32f2f",
                        }}
                      >
                        {unitDateTimeError}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                {/* PROVISIONAL */}
                <TableCell>
                  <Checkbox
                    checked={Boolean(unit.provisional)}
                    disabled={!selected}
                    onChange={(event) =>
                      handleUnitFieldChange(
                        unit.id,
                        "provisional",
                        event.target.checked,
                      )
                    }
                    size="small"
                  />
                </TableCell>
                {/* INVOICED UP TO */}{" "}
                <TableCell>
                  {" "}
                  <TextField
                    type="date"
                    size="small"
                    value={formatDateForInput(
                      unit.invoicedUpto || unit.invoicedUpTo || "",
                    )}
                    onChange={(event) =>
                      handleUnitFieldChange(
                        unit.id,
                        "invoicedUpto",
                        event.target.value,
                      )
                    }
                    disabled={!selected}
                    sx={{
                      width: "150px",
                      "& .MuiInputBase-input": {
                        fontSize: "13px",
                        height: "20px",
                      },
                    }}
                  />{" "}
                </TableCell>
              </TableRow>
            );
          })
        ) : (
          <TableRow>
            <TableCell colSpan={tableHeadings.length} align="center">
              No units found for this storage.
            </TableCell>
          </TableRow>
        )}
      </TableDesign>

      {/* Continue button is handled by parent */}
      {/* If Continue button is inside this component, use:
          <Button onClick={handleContinue}>Continue</Button>
      */}
    </>
  );
};

export default SelectUnitStep;
